import { useState } from 'react';
import {
  ICONS,
  Button,
  Typography,
  ButtonsWrapper,
  ExpandableTable,
  TYPOGRAPHY_SIZE,
  PLATFORM_THEME_COLORS,
} from '@thinknet-gmbh/platform-common-ui';
import { PERMISSION_ACTIONS, PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';
import { useTranslation } from 'react-i18next';

import { useRouteQuery } from 'src/common/helpers';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetDocumentLabelsListByCompany } from 'src/hooks/documents-labels/get-documents-labels.hook';
import { DocumentFilesList } from 'src/components/document-files-list';
import PermissionCheck from 'src/components/permission-check';

import { DOCUMENTS_PAGE_MODALS } from './documents.constants';
import { MainContainer, HeaderContainer } from './documents.styles';
import { UploadTemplateModal } from './components/upload-template-modal';
import { EditDocumentTemplate } from './components/edit-document-template';
import { PlaceholderListModal } from './components/placeholder-list-modal';

const Documents = () => {
  const { t } = useTranslation(['common', 'settings']);
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const query = useRouteQuery();
  const id: string = query.get('id') || '';

  const closeModal = () => setActiveModal(null);
  const currentCompanyId = useGetCurrentCompanyId();
  const { loading, paginationConfig, documentsLabels, onTableChange } =
    useGetDocumentLabelsListByCompany(currentCompanyId);

  return (
    <MainContainer>
      {id ? (
        <EditDocumentTemplate documentTemplateId={id} />
      ) : (
        <div>
          {activeModal === DOCUMENTS_PAGE_MODALS.placeholderList && (
            <PlaceholderListModal onClose={closeModal} />
          )}
          {activeModal === DOCUMENTS_PAGE_MODALS.uploadTemplate && (
            <UploadTemplateModal onClose={closeModal} />
          )}
          <HeaderContainer>
            <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
              {t('settings:documents.labels.documents')}
            </Typography>
            <ButtonsWrapper>
              <ButtonsWrapper>
                <Button
                  type="primary"
                  ghost
                  icon={ICONS.listUl}
                  iconColor={PLATFORM_THEME_COLORS.primary.blue}
                  onClick={() => setActiveModal(DOCUMENTS_PAGE_MODALS.placeholderList)}
                >
                  {t('settings:documents.buttons.placeholderList')}
                </Button>
                <PermissionCheck
                  permission={PLATFORM_PERMISSIONS.companyManagement}
                  action={PERMISSION_ACTIONS.create}
                >
                  <Button
                    type="primary"
                    icon={ICONS.upload}
                    iconColor={PLATFORM_THEME_COLORS.primary.white}
                    onClick={() => setActiveModal(DOCUMENTS_PAGE_MODALS.uploadTemplate)}
                  >
                    {t('settings:documents.buttons.uploadNewTemplate')}
                  </Button>
                </PermissionCheck>
              </ButtonsWrapper>
            </ButtonsWrapper>
          </HeaderContainer>
          <ExpandableTable
            t={t}
            loading={loading}
            onChange={onTableChange}
            dataSource={documentsLabels}
            paginationConfig={paginationConfig}
            expandedRowRender={document =>
              document.documentTemplates.map((item: any, index: number) => (
                <DocumentFilesList
                  key={item._id}
                  template={item}
                  index={index}
                  documentId={document._id}
                />
              ))
            }
          >
            <ExpandableTable.Column
              title={t('settings:documentsLabels.labels')}
              sorter
              key="name"
              dataIndex="name"
              render={name => <Typography>{name}</Typography>}
            />
          </ExpandableTable>
        </div>
      )}
    </MainContainer>
  );
};

export default Documents;
